import { Step } from "react-joyride";

export interface StepAction {
  selector: string;
  type: 'click' | 'type';
  value?: string;
}

export interface ExtendedStep extends Step {
  data?: {
    action?: StepAction;
  };
}

export interface Feature {
  id: string;
  title: string;
  description: string;
  route?: string;
  steps: ExtendedStep[];
}

export interface AgentConfig {
  projectName: string;
  welcomeMessage: string;
  features: Feature[];
}

export const agentConfig: AgentConfig = {
  projectName: "RoomLoop",
  welcomeMessage: "Hi! I'm your RoomLoop guide. I can show you around or help you with specific features.",
  features: [
    {
      id: "dashboard",
      title: "Dashboard Overview",
      description: "Get a quick summary of your room activities.",
      route: "/dashboard",
      steps: [
        {
          target: "#dashboard-header",
          content: "Welcome to your Dashboard! This is your central hub for managing all your virtual events. Here you can see a high-level overview of your activities and quick stats.",
          placement: "bottom",
        },
        {
          target: "#dashboard-tabs",
          content: "Use these tabs to filter your rooms. Let's check your 'Upcoming' events to see what's on your schedule.",
          placement: "bottom",
          data: {
            action: {
              selector: "#tab-upcoming",
              type: "click"
            }
          }
        },
        {
          target: "#tab-past",
          content: "And here are your 'Past Events'. You can review details and stats from completed rooms here.",
          placement: "bottom",
          data: {
            action: {
              selector: "#tab-past",
              type: "click"
            }
          }
        },
        {
          target: "#tab-all",
          content: "Let's go back to 'All Rooms' to see everything in one place.",
          placement: "bottom",
          data: {
            action: {
              selector: "#tab-all",
              type: "click"
            }
          }
        },
        {
          target: "#dashboard-room-grid",
          content: "Your rooms will appear here in a grid. Each card shows key details like the room title, status, and date. Click on a card to view details or join a live room.",
          placement: "top",
          spotlightClicks: true,
          data: {
            action: {
              selector: ".dashboard-join-btn",
              type: "click"
            }
          }
        },
      ],
    },
    {
      id: "dashboard-chat-demo",
      title: "Dashboard Chat Demo",
      description: "Experience the live chat functionality.",
      // No route defined, runs on current page (RoomView)
      steps: [
        {
          target: "#room-chat",
          content: "Welcome to the Room View! This is where the action happens. On the right, you have the Live Chat.",
          placement: "left",
        },
        {
          target: "#chat-input",
          content: "You can chat with other participants in real-time. Let's say hello!",
          placement: "top",
          data: {
            action: {
              selector: "#chat-input",
              type: "type",
              value: "Hello everyone! Joining from the Dashboard demo! 🚀"
            }
          }
        },
        {
          target: "#chat-send-btn",
          content: "Click the send button to post your message instantly.",
          placement: "left",
          data: {
            action: {
              selector: "#chat-send-btn",
              type: "click"
            }
          }
        },
      ]
    },
    {
      id: "explore",
      title: "Explore Rooms",
      description: "Find public rooms and join communities.",
      route: "/explore",
      steps: [
        {
          target: "#explore-header",
          content: "This is the Explore page. It's the best place to discover public rooms and events happening right now in the RoomLoop community.",
          placement: "bottom",
        },
        {
          target: "#explore-tabs",
          content: "You can switch between 'Public' rooms, which are open to everyone, and 'Private' rooms that you've been invited to.",
          placement: "bottom",
        },
        {
          target: "#explore-search",
          content: "Looking for something specific? Use the search bar to find rooms by title or tag. Let's try searching for 'Music'.",
          placement: "bottom",
          data: {
            action: {
              selector: "#explore-search input",
              type: "type",
              value: "Music"
            }
          }
        },
        {
          target: "#explore-room-list",
          content: "Here are the results. Browse the list of available rooms to find one that interests you.",
          placement: "top",
        },
        {
          target: ".explore-join-btn",
          content: "Found a room you like? Click 'Join' or 'View' to enter the room and start interacting. Let's go in!",
          placement: "top",
          data: {
            action: {
              selector: ".explore-join-btn", // Clicks the first one found
              type: "click"
            }
          }
        },
      ],
    },
    {
      id: "room-demo",
      title: "Room Chat Demo",
      description: "Experience the live chat functionality.",
      // No route defined, so it runs on the current page (which should be RoomView after the previous click)
      steps: [
        {
          target: "#room-chat",
          content: "Welcome to the Room View! This is where the action happens. On the right, you have the Live Chat.",
          placement: "left",
        },
        {
          target: "#chat-input",
          content: "You can chat with other participants in real-time. Let's say hello!",
          placement: "top",
          data: {
            action: {
              selector: "#chat-input",
              type: "type",
              value: "Hello everyone! This is a demo message from the AI Agent. 🤖"
            }
          }
        },
        {
          target: "#chat-send-btn",
          content: "Click the send button to post your message instantly.",
          placement: "left",
          data: {
            action: {
              selector: "#chat-send-btn",
              type: "click"
            }
          }
        },
      ]
    },
    {
      id: "create-room",
      title: "Create a Room",
      description: "Learn how to list a new room.",
      route: "/create-room",
      steps: [
        {
          target: "#create-room-header",
          content: "Ready to host your own event? This is the Create Room page.",
          placement: "bottom",
        },
        {
          target: "#create-room-form",
          content: "Fill out the details to set up your room. Let's give it a catchy title.",
          placement: "right",
          data: {
            action: {
              selector: "input[name='title']",
              type: "type",
              value: "My Awesome Demo Room"
            }
          }
        },
        {
          target: "textarea[name='description']",
          content: "And a short description to let people know what it's about.",
          placement: "right",
          data: {
            action: {
              selector: "textarea[name='description']",
              type: "type",
              value: "This is a demo room created by the AI Agent to show you how it works!"
            }
          }
        },
        {
          target: "#create-room-preview",
          content: "As you type, watch the Live Preview update in real-time. This shows you exactly how your room card will look to others.",
          placement: "left",
        },
      ],
    },
    {
      id: "profile",
      title: "User Profile",
      description: "Manage your account and view your stats.",
      route: "/profile",
      steps: [
        {
          target: "#profile-header",
          content: "Finally, this is your Profile page. It's your personal dashboard for account management.",
          placement: "bottom",
        },
        {
          target: "body",
          content: "Here you can view your user details, edit your profile info, and manage your settings.",
          placement: "center",
        },
        {
          target: "#profile-stats",
          content: "Check out your hosting statistics here. You can track Total rooms, Live events, Scheduled rooms, and Closed sessions.",
          placement: "top",
        },
        {
          target: "#profile-rooms",
          content: "And down here is a complete collection of all the rooms you've ever created.",
          placement: "top",
        },
      ],
    },
  ],
};
